import { useState, useRef, useEffect, useMemo, Fragment } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Pencil, Trash2, MapPin, Clock, Flag, X, ScanSearch, GitMerge, SpellCheck, Languages, MoreHorizontal, History, MessageSquare, EyeOff } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { api, isPublicView } from "@/lib/api";
import { useEntity, useEntities, useInvalidate } from "@/lib/queries";
import { catColor, FLAG_META } from "@/lib/lore";
import { CategoryBadge } from "@/components/CategoryBadge";
import { EntityFormDialog } from "@/components/EntityFormDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { RenameDialog } from "@/components/RenameDialog";
import { MergeDialog } from "@/components/MergeDialog";
import { CompletenessPanel } from "@/components/CompletenessPanel";
import { AgeCard } from "@/components/calendar/AgeCard";
import { ImageGallery } from "@/components/ImageGallery";
import { NotAnIssueButton, DismissedFlags } from "@/components/DismissedFlags";
import { SendToLanguageDialog } from "@/components/language/SendToLanguage";
import { EntityWords } from "@/components/language/EntityWords";
import { EntityQuotes } from "@/components/quotes/EntityQuotes";
import { EntityRecipes } from "@/components/recipes/EntityRecipes";
import { HistoryDialog } from "@/components/HistoryDialog";
import { LiveNotes } from "@/components/rich/LiveNotes";
import { OfflineNotes } from "@/components/rich/OfflineNotes";
import { useIsLocal } from "@/lib/offline/useOffline";
import { LiveSummary, LiveFields } from "@/components/rich/LiveFields";
import { CommentsPanel } from "@/components/comments/CommentsPanel";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { RichView } from "@/components/rich/RichEditor";
import { evDate } from "@/lib/eventDate";

const escapeRx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function LinkedText({ text, entities, selfId, onNavigate, className = "whitespace-pre-wrap leading-relaxed text-[#E8DFD1]", empty = "No notes yet." }) {
  const names = (entities || []).filter((e) => e.id !== selfId).flatMap((e) => [e.name, ...(e.aliases || [])].map((n) => ({ n, e })))
    .filter(({ n }) => n.length > 2).sort((a, b) => b.n.length - a.n.length);
  if (!text) return <p className="text-[#A8A090] italic">{empty}</p>;
  if (!names.length) return <p className={className}>{text}</p>;
  const map = Object.fromEntries(names.map(({ n, e }) => [n.toLowerCase(), e]));
  const parts = text.split(new RegExp(`(\\b(?:${names.map(({ n }) => escapeRx(n)).join("|")})\\b)`, "gi"));
  return (
    <p className={className}>
      {parts.map((p, i) => {
        const e = map[p.toLowerCase()];
        return e ? (
          <button key={i} onClick={() => onNavigate(e.id)} className="underline decoration-dotted underline-offset-4 hover:text-[#F59E0B]" style={{ textDecorationColor: catColor(e.category) }} data-testid={`content-link-${e.id}`}>{p}</button>
        ) : <Fragment key={i}>{p}</Fragment>;
      })}
    </p>
  );
}

function Mentions({ text, entities, selfId, onNavigate }) {
  const low = (text || "").toLowerCase();
  const found = (entities || []).filter((e) => e.id !== selfId && [e.name, ...(e.aliases || [])].some((n) => n.length > 2 && new RegExp(`\\b${escapeRx(n.toLowerCase())}\\b`).test(low)));
  if (!found.length) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs" data-testid="notes-mentions">
      <span className="text-[#A8A090]">Mentions</span>
      {found.map((e) => (
        <button key={e.id} onClick={() => onNavigate(e.id)} className="rounded-full border border-[#2D3442] px-2 py-0.5 text-[#E8DFD1] hover:border-[#D97706]/60 hover:text-[#F59E0B]" style={{ borderColor: `${catColor(e.category)}66` }} data-testid={`content-link-${e.id}`}>{e.name}</button>
      ))}
    </div>
  );
}

const NotesBody = ({ ent, all, canEdit, onNavigate, compact, onLive }) => {
  const [md, setMd] = useState(ent.content || "");
  const [params] = useSearchParams();
  const deep = params.get("comment");
  const [active, setActive] = useState(deep);
  const [scroll, setScroll] = useState(deep ? 1 : 0);
  const [pending, setPending] = useState(null);
  const [sheet, setSheet] = useState(!!deep);
  const [found, setFound] = useState(new Set());
  const qc = useQueryClient();
  const pub = isPublicView();
  const local = useIsLocal(ent.universe_id);
  const { data: comments = [] } = useQuery({ queryKey: ["comments", ent.id], queryFn: () => api.get(`/entities/${ent.id}/comments`).then((r) => r.data), enabled: !pub });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["comments", ent.id] }); qc.invalidateQueries({ queryKey: ["comment-counts", ent.universe_id] }); };
  const pick = (id, fromPanel) => { setActive(id); if (fromPanel) setScroll((n) => n + 1); };
  const openCount = comments.filter((c) => !c.resolved).length;
  const applyRef = useRef(null);
  const cm = useMemo(() => ({ applyRef, list: comments.filter((c) => !c.resolved), active, scroll, onPick: (id) => { setActive(id); setSheet(true); }, onRequest: (a) => { setPending(a); setSheet(true); }, onFound: (f) => setFound((p) => (p.size === f.size && [...f].every((x) => p.has(x)) ? p : f)) }), [comments, active, scroll]);
  const panel = (close) => (
    <CommentsPanel ent={ent} comments={comments} pending={pending} setPending={setPending} active={active} onPick={(id) => pick(id, true)} canEdit={canEdit}
      isOwner={ent.role === "owner"} refresh={refresh} foundIds={found} onClose={close} applyRef={applyRef} />
  );
  if (pub) return (
    <>
      {ent.content ? <RichView markdown={ent.content} testid="entity-notes-view" /> : <p className="text-[#A8A090] italic">No notes yet.</p>}
      <Mentions text={ent.content} entities={all} selfId={ent.id} onNavigate={onNavigate} />
    </>
  );
  const inline = !compact;
  if (local) return (
    <>
      <OfflineNotes key={ent.id} ent={ent} canEdit={canEdit} onMarkdown={setMd} />
      <Mentions text={md} entities={all} selfId={ent.id} onNavigate={onNavigate} />
    </>
  );
  return (
    <div className={inline ? "xl:grid xl:grid-cols-[1fr_300px] xl:gap-6" : ""}>
      <div className="min-w-0">
        <div className={`mb-2 flex justify-end ${inline ? "xl:hidden" : ""}`}>
          <button onClick={() => setSheet(true)} className="flex items-center gap-1.5 rounded-full border border-[#2D3442] px-3 py-1 text-xs text-[#E8DFD1] hover:border-[#D97706]/60" data-testid="comments-open-button">
            <MessageSquare className="h-3.5 w-3.5 text-[#F59E0B]" />Comments{openCount ? ` · ${openCount}` : ""}
          </button>
        </div>
        <LiveNotes ent={ent} canEdit={canEdit} onMarkdown={setMd} comments={cm} onLive={onLive} />
        <Mentions text={md} entities={all} selfId={ent.id} onNavigate={onNavigate} />
      </div>
      {inline && <div className="hidden xl:block">{panel(null)}</div>}
      <Sheet open={sheet && !(inline && window.innerWidth >= 1280)} onOpenChange={setSheet}>
        <SheetContent side="right" className="w-full overflow-y-auto border-[#2D3442] bg-[#0B0D10] p-4 sm:max-w-sm lf-scroll" data-testid="comments-sheet">
          <SheetTitle className="sr-only">Comments</SheetTitle>
          {panel(() => setSheet(false))}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const Section = ({ title, children, i }) => (
  <motion.section initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }} className="mt-8">
    <div className="lf-label mb-3">{title}</div>
    {children}
  </motion.section>
);

const LifeDateNotes = ({ ent, canEdit }) => {
  const items = Object.entries(ent.life_dates || {}).filter(([, v]) => canEdit || v.status !== "unplaced");
  if (!items.length) return null;
  return (
    <div className="mt-3 space-y-1.5" data-testid="life-date-notes">
      {items.map(([k, v]) => v.status === "unplaced" ? (
        <p key={k} className="text-xs text-[#FBBF24]" data-testid={`life-date-unplaced-${k}`}>Couldn't place this {k} date (“{v.src}”) on a calendar. Try a format like “March 8, 1929” or a date from one of your calendars, or <Link to={`/u/${ent.universe_id}/timelines?tab=calendars`} className="underline underline-offset-2 hover:text-[#F59E0B]" data-testid={`life-date-fix-${k}`}>set up a calendar</Link>.</p>
      ) : (
        <p key={k} className="text-xs text-[#A8A090]" data-testid={`life-date-placed-${k}`}>{k === "birth" ? "Born" : "Died"} {v.label}{v.timeline ? ` · on ${v.timeline}` : ""}{v.calendar_event_id ? ` · birthday on ${v.calendar}` : ""}</p>
      ))}
    </div>
  );
};

function FlagList({ flags, canEdit, onNavigate, onDismissed }) {
  const dismiss = async (f) => {
    await (f.virtual ? api.post(`/entities/${f.id.slice(4)}/dismiss-dob-flag`) : api.delete(`/flags/${f.id}`));
    onDismissed();
  };
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-2" data-testid="entity-flags">
      {flags.map((f) => {
        const m = FLAG_META[f.type];
        return (
          <div key={f.id} className="rounded-md border px-4 py-3 text-sm" style={{ borderColor: `${m.color}55`, background: `${m.color}0F` }} data-testid={`entity-flag-${f.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em]" style={{ color: m.color }}><Flag className="h-3.5 w-3.5" />{m.label}</div>
              {canEdit && (
                <div className="flex items-center gap-3">
                  {!f.virtual && <NotAnIssueButton flag={f} onDone={onDismissed} />}
                  <button onClick={() => dismiss(f)} className="text-[#A8A090] hover:text-[#F3EBDD]" title="Clear for now (may come back on the next review)" data-testid={`dismiss-flag-${f.id}`}><X className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
            <p className="mt-1.5 text-[#E8DFD1] leading-relaxed">{f.message}</p>
            {f.related?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5 text-xs text-[#A8A090]">See:
                {f.related.map((r) => <button key={r.id} onClick={() => onNavigate(r.id)} className="underline underline-offset-2 hover:text-[#F59E0B]">{r.name}</button>)}
              </div>
            )}
          </div>
        );
      })}
    </motion.div>
  );
}

export const HiddenBadge = ({ className = "", testid }) => (
  <span className={`inline-flex items-center gap-1 rounded-sm border border-[#A78BFA]/40 bg-[#A78BFA]/10 px-1.5 py-0.5 align-middle text-[10px] uppercase tracking-widest text-[#C4B5FD] ${className}`}
    title="Hidden: only owners and editors can see this; left out of exports" data-testid={testid}><EyeOff className="h-3 w-3" />Hidden</span>
);

const EntityChip = ({ e, onNavigate }) => (
  <button onClick={() => onNavigate(e.id)} data-testid={`entity-link-${e.id}`}
    className="inline-flex items-center gap-1.5 rounded-sm border border-[#2D3442] bg-[#1A202C] px-2.5 py-1 text-sm text-[#E8DFD1] transition-colors hover:border-[#D97706]/60 hover:text-[#F3EBDD]">
    <span className="h-1.5 w-1.5 rounded-full" style={{ background: catColor(e.category) }} />{e.name}
    {e.hidden && <EyeOff className="h-3 w-3 text-[#C4B5FD]" />}
  </button>
);

const useWide = (on) => {
  const q = "(min-width: 1536px)";
  const [w, setW] = useState(() => on && window.matchMedia(q).matches);
  useEffect(() => {
    if (!on) return undefined;
    const m = window.matchMedia(q), f = () => setW(m.matches);
    m.addEventListener("change", f);
    return () => m.removeEventListener("change", f);
  }, [on]);
  return on && w;
};

export const EntityDetail = ({ entityId, onNavigate, compact, onDeleted }) => {
  const wide = useWide(!compact);
  const { data: ent, isLoading, isError } = useEntity(entityId);
  const reviewing = ent?.review_status === "running";
  const [updatedBy, setUpdatedBy] = useState(null);
  useEffect(() => {
    const on = (e) => e.detail.entity_id === entityId && setUpdatedBy(e.detail.by);
    window.addEventListener("lf-change", on);
    return () => window.removeEventListener("lf-change", on);
  }, [entityId]);
  const wasReviewing = useRef(false);
  useEffect(() => {
    if (wasReviewing.current && !reviewing && ent) invalidate(["entities", ent.universe_id], ["suggestions", ent.universe_id], "entity");
    wasReviewing.current = reviewing;
  }, [reviewing]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data: all } = useEntities(ent?.universe_id);
  const invalidate = useInvalidate();
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [merging, setMerging] = useState(false);
  const [sending, setSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [live, setLive] = useState(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const uniData = queryClient.getQueryData(["universe", ent?.universe_id]);
  const f = uniData?.features;
  const mods = { ...(uniData?.modules || {}), ...(f ? { maps: f.includes("maps") && uniData.modules?.maps !== false, timelines: f.includes("timelines") && uniData.modules?.timelines !== false, languages: f.includes("languages") && uniData.modules?.languages !== false } : {}) };
  if (isError && !ent) return <div className="rounded-md border border-dashed border-[#2D3442] p-10 text-center" data-testid="entity-not-found"><p className="font-display text-2xl text-[#F3EBDD]">Entry not found</p><p className="mt-1 text-sm text-[#A8A090]">It may have been deleted, or it isn't available to you.</p></div>;
  if (isLoading || !ent) return <div className="text-[#A8A090]">Loading entry…</div>;
  const canEdit = ["owner", "editor"].includes(ent.role) && !isPublicView();
  const attrs = Object.entries(ent.attributes || {});

  const remove = async () => {
    await api.delete(`/entities/${ent.id}`);
    toast.success(`${ent.name} removed from the bible`);
    invalidate(["entities", ent.universe_id]);
    onDeleted?.();
  };

  return (
    <div data-testid="entity-detail">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <CategoryBadge category={ent.category} />
          {ent.is_stub && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#A8A090]">stub</span>}
          {ent.hidden && <HiddenBadge className="ml-2" testid="entity-hidden-badge" />}
          <h1 className={`font-display mt-3 text-[#F3EBDD] leading-none break-words ${compact ? "text-4xl" : "text-4xl sm:text-5xl lg:text-6xl"}`} data-testid="entity-name">{ent.name}</h1>
          {ent.category === "Term" && ent.attributes?.meaning && <p className="mt-3 font-display text-lg text-[#D6CCBC]" data-testid="term-meaning"><span className="mr-2 text-xs uppercase tracking-[0.16em] text-[#84CC16]">Meaning</span>{ent.attributes.meaning}</p>}
        </div>
        {canEdit && (
          <div className="flex gap-1 shrink-0 sm:hidden">
            <Button size="icon" variant="outline" className="h-10 w-10" onClick={() => setEditing(true)} data-testid="entity-edit-button-mobile"><Pencil className="h-4 w-4" /></Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-10 w-10" data-testid="entity-more-menu"><MoreHorizontal className="h-5 w-5" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setRenaming({})} data-testid="entity-more-rename"><SpellCheck className="h-4 w-4 mr-2" />Rename everywhere</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowHistory(true)} data-testid="entity-more-history"><History className="h-4 w-4 mr-2" />History & restore</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setMerging(true)} data-testid="entity-more-merge"><GitMerge className="h-4 w-4 mr-2" />Merge entries into this</DropdownMenuItem>
                {mods.languages !== false && <DropdownMenuItem onClick={() => setSending(true)} data-testid="entity-more-send"><Languages className="h-4 w-4 mr-2" />Send to a language</DropdownMenuItem>}
                <DropdownMenuItem onClick={() => setConfirm(true)} className="text-red-400" data-testid="entity-more-delete"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        {canEdit && (
          <div className="hidden gap-1 shrink-0 sm:flex">
            <Button size="icon" variant="ghost" onClick={() => setEditing(true)} data-testid="entity-edit-button"><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => setRenaming({})} title="Rename everywhere" data-testid="entity-rename-button"><SpellCheck className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => setShowHistory(true)} title="History & restore" data-testid="entity-history-button"><History className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => setMerging(true)} title="Merge other entries into this one" data-testid="entity-merge-button"><GitMerge className="h-4 w-4" /></Button>
            {mods.languages !== false && <Button size="icon" variant="ghost" onClick={() => setSending(true)} title="Send to a language" data-testid="entity-send-to-language"><Languages className="h-4 w-4" /></Button>}
            <Button size="icon" variant="ghost" onClick={() => setConfirm(true)} data-testid="entity-delete-button"><Trash2 className="h-4 w-4 text-red-400" /></Button>
          </div>
        )}
      </div>
      {!canEdit && !isPublicView() && (
        <button onClick={() => setShowHistory(true)} className="mt-3 flex items-center gap-1 text-xs text-[#A8A090] hover:text-[#F59E0B]" data-testid="entity-history-view"><History className="h-3.5 w-3.5" />View history</button>
      )}
      {ent.aliases?.length > 0 && <p className="mt-3 text-sm text-[#A8A090]" data-testid="entity-aliases">Also known as {ent.aliases.join(", ")}</p>}
      <div className="mt-5" data-testid="entity-summary">
        {live ? <LiveSummary live={live} editable={canEdit && !live.init.readonly} />
          : <LinkedText text={ent.summary} entities={all} selfId={ent.id} onNavigate={onNavigate} empty="No summary yet." className="font-display text-xl italic text-[#E8DFD1] leading-snug" />}
      </div>
      {ent.language_id && mods.languages !== false && (
        <Button variant="outline" size="sm" className="mt-5 border-[#14B8A6]/50 text-[#5EEAD4]" data-testid="entity-open-language"
          onClick={() => navigate(`${window.location.pathname.split("/").slice(0, 3).join("/")}/languages/${ent.language_id}`)}>
          <Languages className="h-4 w-4 mr-1.5" />Open grammar & dictionary
        </Button>
      )}
      {updatedBy && <p className="mt-3 text-xs text-[#10B981]" data-testid="entity-updated-by">Updated by {updatedBy} just now</p>}
      {reviewing && (
        <div className="mt-5 flex items-center gap-2 rounded-md border border-[#D97706]/30 bg-[#D97706]/5 px-4 py-2.5 text-sm text-[#F59E0B]" data-testid="entity-rechecking">
          <ScanSearch className="h-4 w-4 animate-pulse" />Re-checking continuity after your edit. Old flags and stub suggestions from this entry will be replaced.
        </div>
      )}
      {ent.flags?.length > 0 && <FlagList flags={ent.flags} canEdit={canEdit} onNavigate={onNavigate} onDismissed={() => invalidate(["entity", ent.id], ["entities", ent.universe_id], ["dismissed-flags", ent.id])} />}
      <DismissedFlags ent={ent} canEdit={canEdit} />

      {(() => {
        const details = (<>
      <CompletenessPanel ent={ent} canEdit={canEdit} onFill={() => setEditing(true)} />
      {ent.category === "Character" && mods.timelines !== false && <AgeCard ent={ent} />}
      {live && (
        <Section title="Attributes" i={1}><LiveFields live={live} editable={canEdit && !live.init.readonly} ent={ent} /></Section>
      )}
      {!live && attrs.length > 0 && (
        <Section title="Attributes" i={1}>
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm border-l-2 border-[#D97706]/40 pl-4" data-testid="entity-attributes">
            {attrs.map(([k, v]) => (
              <Fragment key={k}><dt className="text-[#A8A090] capitalize">{k.replace(/_/g, " ")}</dt><dd className="text-[#F3EBDD]">{v}</dd></Fragment>
            ))}
          </dl>
        </Section>
      )}
      {mods.timelines !== false && <LifeDateNotes ent={ent} canEdit={canEdit} />}
      {(ent.related.length > 0 || ent.referenced_by.length > 0) && (
        <Section title="Connections" i={3}>
          <div className="flex flex-wrap gap-2" data-testid="entity-related">
            {[...ent.related, ...ent.referenced_by.filter((r) => !ent.related.some((x) => x.id === r.id))].map((e) => <EntityChip key={e.id} e={e} onNavigate={onNavigate} />)}
          </div>
        </Section>
      )}
      {ent.events.length > 0 && mods.timelines !== false && (
        <Section title="Appears in events" i={4}>
          <ol className="space-y-3" data-testid="entity-events">
            {ent.events.map((ev) => (
              <li key={ev.id} className="flex gap-3 text-sm">
                <Clock className="h-4 w-4 mt-0.5 text-[#D97706] shrink-0" />
                <div><div className="text-[#F3EBDD]">{ev.title}</div><div className="text-xs text-[#A8A090]">{evDate(ev)} · {ev.timeline_name}</div></div>
              </li>
            ))}
          </ol>
        </Section>
      )}
      {(ent.pins.length > 0 || ent.regions?.length > 0) && mods.maps !== false && (
        <Section title="On the map" i={5}>
          <div className="space-y-2" data-testid="entity-pins">
            {(ent.regions || []).map((r) => (
              <div key={r.id} className="flex items-center gap-2 text-sm text-[#E8DFD1]" data-testid={`entity-region-${r.id}`}><span className="h-3 w-3 rounded-sm" style={{ background: r.color }} />{r.name} <span className="text-[#A8A090]">· region · {r.map_name}</span></div>
            ))}
            {ent.pins.map((p) => (
              <div key={p.id} className="flex items-center gap-2 text-sm text-[#E8DFD1]"><MapPin className="h-4 w-4 text-[#10B981]" />{p.label || ent.name} <span className="text-[#A8A090]">· {p.map_name}</span></div>
            ))}
          </div>
        </Section>
      )}
        </>);
        const main = (<>
      <Section title="Notes" i={2}><NotesBody key={ent.id} ent={ent} all={all} canEdit={canEdit} onNavigate={onNavigate} compact={compact} onLive={setLive} /></Section>
      <EntityQuotes ent={ent} canEdit={canEdit} onNavigate={onNavigate} />
      <EntityRecipes ent={ent} canEdit={canEdit} />
      {mods.languages !== false && <EntityWords ent={ent} />}
      <ImageGallery images={ent.images || []} canEdit={canEdit} onSave={async (images) => {
        await api.put(`/entities/${ent.id}/images`, { images });
        invalidate(["entity", ent.id], ["entities", ent.universe_id]);
      }} />
        </>);
        if (!wide) return <>{details}{main}</>;
        return (
          <div className="grid grid-cols-[minmax(0,1fr)_340px] gap-10" data-testid="entity-wide-layout">
            <div className="min-w-0">{main}</div>
            <aside className="min-w-0 border-l border-[#2D3442] pl-8" data-testid="entity-side-column">{details}</aside>
          </div>
        );
      })()}
      {editing && <EntityFormDialog open onOpenChange={setEditing} uid={ent.universe_id} initial={ent} onSaved={(d) => d.name !== ent.name && setRenaming({ oldName: ent.name, newName: d.name })} />}
      {renaming && <RenameDialog open onOpenChange={() => setRenaming(null)} ent={ent} oldName={renaming.oldName} newName={renaming.newName} />}
      {merging && <MergeDialog open onOpenChange={setMerging} uid={ent.universe_id} targetId={ent.id} />}
      {sending && <SendToLanguageDialog ent={ent} onOpenChange={setSending} />}
      {showHistory && <HistoryDialog ent={ent} canEdit={canEdit} onClose={() => setShowHistory(false)} />}
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} onConfirm={remove} title={`Delete ${ent.name}?`} description="Links, event references and map pins pointing to this entry will be cleared." />
    </div>
  );
};
